import React from 'react';
import { Link } from 'react-router-dom';
import { FiCheck, FiArrowRight, FiMapPin, FiSun, FiHeart, FiStar, FiHome, FiUsers } from 'react-icons/fi';
import OtherOccasions from '../components/OtherOccasions';
import SEO from '../components/SEO';

// Family Weekend Getaway — SEO/GEO landing page for queries like:
// "villas in Lonavala for family weekend getaway",
// "best villa for family trip Lonavala", "kid friendly villa Lonavala"
const FamilyGetaway = () => {
  return (
    <div data-testid="family-getaway-page" className="pt-20">
      <SEO
        title="Best Place to Stay in Lonavala with Family — Family Staycation Private Pool Villas | Royal Garden Villas"
        description="Plan your family weekend getaway in Lonavala at Royal Garden Villas — 3BHK to 12BHK luxury villas with private pools, kid-safe areas, housekeeping, 24/7 security and BBQ setups — a family staycation 2 hrs from Mumbai, 90 min from Pune."
        path="/family-weekend-getaway-lonavala"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Family Weekend Getaways', url: '/family-weekend-getaway-lonavala' },
        ]}
      />
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[400px] overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/bqiogpix_RG%20Banner.png"
          alt="Family weekend getaway at Royal Garden Villas Lonavala"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A2E]/85 via-[#1A1A2E]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-7xl mx-auto">
          <p className="text-[#F57C20] text-xs font-semibold tracking-[0.2em] uppercase mb-3">Family Trips</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Luxury Family Weekend <span className="text-[#F57C20]">Getaways in Lonavala</span>
          </h1>
          <p className="text-white/85 font-light mt-4 max-w-2xl text-sm md:text-base leading-relaxed">
            Private pool villas for unforgettable family trips — kid-safe pools, vast lawns, breakfast served in your living room, and a caretaker dedicated to your villa for the entire stay.
          </p>
        </div>
      </section>

      {/* Why families choose us */}
      <section className="py-16 md:py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl font-light text-[#1A1A2E] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Why Families <span className="text-[#F57C20]">Choose Royal Garden Villas</span>
          </h2>
          <p className="text-[#6B7280] text-sm leading-relaxed max-w-3xl mb-10">
            Looking for the ideal <strong>villa in Lonavala for a family weekend getaway</strong>? Royal Garden Villas is a gated estate of luxury private pool villas designed for multi-generational family trips — comfortable for grandparents, fun for children, and effortless for parents who want a real break.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: FiUsers, title: 'Multi-generation friendly', desc: 'Ground-floor bedrooms, elder-safe bathrooms, dining tables for 12–16 guests.' },
              { icon: FiSun, title: 'Kid-safe private pools', desc: 'Pool depths suitable for children with supervision. No shared pools.' },
              { icon: FiHome, title: 'Fully equipped kitchen', desc: 'Cook your own meals or hire our maid service. Burner, fridge, microwave, utensils — all stocked.' },
              { icon: FiStar, title: 'Dedicated caretaker', desc: 'Full-time helper assigned to your villa for the entire stay.' },
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-xl border border-gray-100">
                <item.icon className="w-6 h-6 text-[#F57C20] mb-4" />
                <h3 className="text-[#1A1A2E] text-sm font-semibold mb-2">{item.title}</h3>
                <p className="text-[#6B7280] text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Long-form content */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 prose-style">
          <h2 className="text-2xl font-light text-[#1A1A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            The Perfect Lonavala Weekend for Your Family
          </h2>
          <div className="space-y-4 text-[#374151] text-sm md:text-base leading-relaxed">
            <p>
              A weekend trip with the family is supposed to feel restful — not like a logistics project. At Royal Garden Villas in Lonavala, we&apos;ve designed our private pool villas so that everything just works for families travelling with kids, elderly parents, or both. The estate is gated, the pools are private (never shared with strangers), and a dedicated caretaker is on-site throughout your stay to help with anything you need.
            </p>
            <p>
              Our most popular family villa choice is the <Link to="/villas/3bhk-garden-escape-lonavala" className="text-[#F57C20] hover:underline">3BHK Garden Escape</Link>, comfortable for up to 12 guests including children. For larger family groups of 12–16, the <Link to="/villas/4bhk-garden-retreat-lonavala" className="text-[#F57C20] hover:underline">4BHK Garden Retreat</Link> is ideal. When extended families travel together (15+ guests, perhaps with cousins and grandparents under one roof), the <Link to="/villas/12bhk-celebration-villa-lonavala" className="text-[#F57C20] hover:underline">12BHK Celebration Villa</Link> is unmatched — you get exclusive use of the entire property.
            </p>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">A Typical Family Weekend at Royal Garden Villas</h3>
            <p>
              <strong>Friday afternoon:</strong> Drive down from Mumbai (90 minutes) or Pune (70 minutes) via the Mumbai–Pune Expressway. Check in from 1:00 PM. The caretaker shows you around, hands over the complimentary Wi-Fi password, and explains the BBQ pit setup if you&apos;ve booked one.
            </p>
            <p>
              <strong>Friday evening:</strong> Settle in. Kids splash in the private pool while parents unwind on the lawn with a cup of cutting chai. As the sun sets behind the Sahyadri ridge, fire up the bonfire (add-on Rs. 2,000 per session) and let the marshmallows toast.
            </p>
            <p>
              <strong>Saturday:</strong> Sleep in. Order breakfast through the caretaker — eggs, parathas, idlis, anything the family wants. Mid-morning, take a short 20-minute drive to Bushi Dam, Lion&apos;s Point, or Tiger Point for a quick sightseeing round. Be back by lunch. Afternoon: pool time, board games, an indulgent nap. Evening: optional BBQ dinner on the lawn (Rs. 3,000 per meal with all setup).
            </p>
            <p>
              <strong>Sunday:</strong> A leisurely brunch, one last swim, pack up by 10:00 AM. Stop at Mapro Garden or the strawberry farms on your way back. Home by sunset.
            </p>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">Add-ons We Recommend for Family Trips</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Extra Butler service (Optional)</strong> (₹2,000/day) — saves you the cooking and cleaning hassle so you can actually relax.</li>
              <li><strong>BBQ setup (Optional)</strong> (₹3,000/meal) — kids love it, parents enjoy it, makes for a memorable family dinner under the stars.</li>
              <li><strong>Bonfire (Optional)</strong> (₹2,000/session) — perfect for winter weekends (October to February) and post-monsoon evenings.</li>
            </ul>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">When to Visit Lonavala for a Family Trip</h3>
            <p>
              Lonavala is enjoyable year-round, but the best time for a family weekend getaway depends on your preference: <strong>monsoon (June–September)</strong> for lush green valleys, waterfalls, and dramatic mist; <strong>winter (October–February)</strong> for crisp clear days, bonfire-perfect evenings, and pleasant pool temperatures; <strong>summer (March–May)</strong> for the warmest pool weather and shortest travel times. Weekends and long weekends book out 4–6 weeks in advance — we recommend planning ahead.
            </p>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">What&apos;s Included in Every Family Stay</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Private swimming pool — cleaned daily, just for your villa</li>
              <li>Fully equipped kitchen with utensils, gas, refrigerator and microwave</li>
              <li>Complimentary Wi-Fi and Smart TVs in living rooms</li>
              <li>Air-conditioned bedrooms with fresh linen and towels</li>
              <li>Dedicated caretaker on the property throughout your stay</li>
              <li>Daily housekeeping (10 AM – 5 PM) for brooming and mopping</li>
              <li>Ample parking inside the gated property</li>
              <li>BBQ pit and bonfire area (setup as paid add-on)</li>
            </ul>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">House Rules That Make Family Stays Worry-Free</h3>
            <p>
              We&apos;re a family-friendly property with sensible house rules — no loud music after 10:30 PM (so the kids in the next villa can sleep), no pets (for hygiene reasons), and a strict no-cash policy with everything paid online. Pool supervision for kids is the parent&apos;s responsibility. Full house rules are shared at the time of booking.
            </p>
          </div>
        </div>
      </section>

      {/* Villa picker */}
      <section className="py-14 md:py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl font-light text-[#1A1A2E] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            Pick the Right <span className="text-[#F57C20]">Family Villa</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { name: '3BHK Garden Escape', cap: 'Small families (up to 12 guests)', slug: '3bhk-garden-escape-lonavala' },
              { name: '4BHK Garden Retreat', cap: 'Mid-size families (up to 16 guests)', slug: '4bhk-garden-retreat-lonavala' },
              { name: '5BHK Garden Delight', cap: 'Larger families (up to 20 guests)', slug: '5bhk-garden-delight-lonavala' },
              { name: '12BHK Celebration Villa', cap: 'Extended families & cousins (up to 48)', slug: '12bhk-celebration-villa-lonavala' },
            ].map((v) => (
              <Link key={v.slug} to={`/villas/${v.slug}`} className="block bg-white rounded-xl p-5 border border-gray-100 hover:border-[#F57C20] transition-all" data-testid={`family-villa-${v.slug}`}>
                <FiHome className="w-5 h-5 text-[#F57C20] mb-3" />
                <h3 className="text-[#1A1A2E] text-base font-semibold mb-1">{v.name}</h3>
                <p className="text-[#6B7280] text-xs mb-3">{v.cap}</p>
                <span className="text-[#F57C20] text-xs font-semibold inline-flex items-center gap-1">View Details <FiArrowRight className="w-3 h-3" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Internal linking — other occasions */}
      <OtherOccasions currentPath="/family-weekend-getaway-lonavala" heading="Also Perfect For" />

      {/* CTA */}
      <section className="py-16 border-t border-white/5 bg-[#1A1A2E]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <FiHeart className="w-6 h-6 text-[#F57C20] mx-auto mb-3" />
          <h3 className="text-xl font-light text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Book Your Family Weekend</h3>
          <p className="text-white/50 font-light mb-8 text-sm max-w-xl mx-auto">Live availability and instant confirmation. 50% advance now, 50% on arrival.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking" className="bg-[#F57C20] text-white px-8 py-3 rounded-lg text-sm font-semibold hover:bg-[#E06A10] transition-all flex items-center justify-center gap-2" data-testid="family-cta-book">
              Check Availability <FiArrowRight />
            </Link>
            <a href="tel:+919820088292" className="border-2 border-white/20 text-white px-8 py-3 rounded-lg text-sm font-semibold hover:border-[#F57C20] hover:text-[#F57C20] transition-all flex items-center justify-center gap-2">
              <FiMapPin className="w-4 h-4" /> Call +91 98200 88292
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FamilyGetaway;
